/**
 * @fileoverview Helper functions for using SpecifyListingConfiguration in the Taskflow.
 *
 * @author Heesun Jun (hjun@yext.com)
 */
import yext from 'goog:yext';

import {ApplePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {FacebookPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/facebookconfiguration';
import {GooglePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpconfiguration';
import {YelpEnterprisePublisherId, YelpKnowledgePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/yelpconfiguration';
import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {SpecifyListingConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/specifylistingconfiguration';
import {BingPublisherId, EnterpriseConfiguration, ExtendedNetworkPublisherId, LlmPublisherId} from
  '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {Task} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/task';
import {TaskIds} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskids';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/taskflow.module.scss';
import BookSvg from '/src/com/yext/listingslaunchstorm/public/icons/book.svg';
import SpecifyListingConfigurationSvg from '/src/com/yext/listingslaunchstorm/public/icons/specify-listing-config-large.svg';

export const SpecifyListingConfigurationTask = new Task({
  id: TaskIds.SPECIFY_LISTING_CONFIGURATION,

  getPage: (enterpriseConfiguration, setEnterpriseConfiguration, _onSkip,
    hasChooseEntitiesAndListingsConfigSetup, publisherIds) => ({
    id: TaskIds.SPECIFY_LISTING_CONFIGURATION,
    layoutHeader: {
      icon: <SpecifyListingConfigurationSvg />,
      title: yext.msg('Specify Listings Configuration'),
      description: getHeaderDescription(),
      resourceLink: {
        src: 'https://hitchhikers.yext.com/tracks/listings/',
        external: true,
        displayText: yext.msg('Learn About Listings Configuration'),
        icon: (<BookSvg />),
      },
    },
    content: (<SpecifyListingConfiguration
      enterpriseConfiguration={enterpriseConfiguration}
      selectedListingConfig={enterpriseConfiguration.listingConfiguration}
      onChangeListingConfig={listingConfiguration => setEnterpriseConfiguration(
        new EnterpriseConfiguration({...enterpriseConfiguration, listingConfiguration}),
      )}
      hasChooseEntitiesAndListingsConfigSetup={hasChooseEntitiesAndListingsConfigSetup}
      publisherIds = {processPublisherIds(publisherIds)} />
    ),
  }),

  getCard: (enterpriseConfiguration, index, selectedPageId, setSelectedPageId,
    disabled, hasChooseEntitiesAndListingsConfigSetup, publisherIds) => ({
    id: TaskIds.SPECIFY_LISTING_CONFIGURATION,
    title: yext.msg('Specify Listings Configurations'),
    index,
    isSelected: (selectedPageId == TaskIds.SPECIFY_LISTING_CONFIGURATION),
    onClick: disabled ? undefined : () => setSelectedPageId(TaskIds.SPECIFY_LISTING_CONFIGURATION),
    content: getCardContent(
      enterpriseConfiguration, hasChooseEntitiesAndListingsConfigSetup, processPublisherIds(publisherIds)),
  }),

  isComplete: enterpriseConfiguration => (enterpriseConfiguration.listingConfiguration !== undefined),
  isSectionSkipped: () => false,
});

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {boolean} hasChooseEntitiesAndListingsConfigSetup
 * @param {Array<number>} publisherIds
 * @returns {React.ReactNode}
 */
function getCardContent(enterpriseConfiguration, hasChooseEntitiesAndListingsConfigSetup, publisherIds) {
  const listingConfig = enterpriseConfiguration.listingConfiguration;

  if (!listingConfig) {
    return undefined;
  } else {
    const altBrandId = listingConfig.profileField?.id;
    const altBrandName = listingConfig.profileField?.name;
    const listingsUpdateSelectedPubs = hasChooseEntitiesAndListingsConfigSetup
      ? getListingsUpdateEnabledPubs(
        listingConfig.deliveryConfigurationMap,
        publisherIds).join(', ')
      : getListingsUpdateDisabledPubs(
        listingConfig.deliveryConfigurationMap,
        publisherIds).join(', ');
    const listingsCreationSelectedPubs = hasChooseEntitiesAndListingsConfigSetup
      ? getListingsCreationEnabledPubs(
        listingConfig.deliveryConfigurationMap,
        publisherIds).join(', ')
      : getListingsCreationDisabledPubs(
        listingConfig.deliveryConfigurationMap,
        publisherIds).join(', ');
    const automaticallyOptInEnabledPubs =
      getAutomaticallyOptInEnabledPubs(
        enterpriseConfiguration,
        listingConfig.deliveryConfigurationMap,
        publisherIds).join(', ');

    return (
      <>
        <div>
          {altBrandId
           && <SelectedValue
             label={yext.msg('Alternate Brand Name Fields: ')}
             value={altBrandName}
           />}
          {listingsUpdateSelectedPubs.length > 0
           && <SelectedValue
             label={yext.msg('Listings Updates: ')}
             value={listingsUpdateSelectedPubs}
           />}
          {listingsCreationSelectedPubs.length > 0
           && <SelectedValue
             label={yext.msg('Listings Creation: ')}
             value={listingsCreationSelectedPubs}
           />}
          {hasChooseEntitiesAndListingsConfigSetup
            && automaticallyOptInEnabledPubs.length > 0
            && <SelectedValue
              label={yext.msg('Automatically Opt in: ')}
              value={automaticallyOptInEnabledPubs}
            />}
        </div>
      </>
    );
  }
}

function SelectedValue({label, value}) {
  return (
    <div className={styles.cardContent}>
      <b>{label}</b>
      {value}
    </div>
  );
}

/** @returns {React.ReactNode} */
function getHeaderDescription() {
  return (
    <div className={styles.headerContent}>
      <p>{yext.msg('This step is optional, you may proceed without any configuration. All of these settings can be found and reconfigured later, outside of this setup, under Listings > Configuration.')}</p>
      <p>{yext.msg('Listings Sync Settings allow you to require manual approval before syncing data to Listings.')}</p>
    </div>
  );
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsUpdateDisabledPubs(configs, publisherIds) {
  const disabledPubs = [];
  publisherIds.forEach(pubId => {
    if (configs.get(pubId)?.dontSyncListings && getDisplayName(pubId)) {
      disabledPubs.push(getDisplayName(pubId));
    }
  });
  return disabledPubs;
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsUpdateEnabledPubs(configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    if (!configs.get(pubId)?.dontSyncListings && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsCreationDisabledPubs(configs, publisherIds) {
  const disabledPubs = [];
  publisherIds.forEach(pubId => {
    if (configs.get(pubId)?.dontCreateNewListings && getDisplayName(pubId)) {
      disabledPubs.push(getDisplayName(pubId));
    }
  });
  return disabledPubs;
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsCreationEnabledPubs(configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    if (!configs.get(pubId)?.dontCreateNewListings && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getAutomaticallyOptInEnabledPubs(enterpriseConfiguration, configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    const publisherEnterpriseConfiguration = enterpriseConfiguration.getEnterpriseConfigurationForPublisherId(pubId);
    if (configs.get(pubId)?.automaticallyOptIn && !publisherEnterpriseConfiguration?.skipSetup && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

function getDisplayName(publisherId) {
  switch (publisherId) {
    case ExtendedNetworkPublisherId:
      return yext.msg('Extended Network');
    case LlmPublisherId:
      return yext.msg('Generative AI Publishers');
    case FacebookPublisherId:
      return 'Facebook';
    case GooglePublisherId:
      return 'Google Business Profile';
    case BingPublisherId:
      return 'Bing';
    case ApplePublisherId:
      return 'Apple';
    case YelpEnterprisePublisherId:
      return 'Yelp';
    default:
      return null;
  }
}

/** @returns {!Array<number>} */
function processPublisherIds(publisherIds) {
  // As long as the account has either Yelp Enterprise or Yelp Knowledge, we will show one row
  // for Yelp that applies for both. Here we manipulate the array to make sure it only
  // contains the Yelp Enterprise publisher ID if it has either.
  const YELP_PUB_IDS = [YelpEnterprisePublisherId, YelpKnowledgePublisherId];
  const includeYelp = publisherIds.some(pubId => YELP_PUB_IDS.includes(pubId));
  const publisherIdsWithoutYelp = publisherIds.filter(pubId => !YELP_PUB_IDS.includes(pubId));
  const corePublishers = (includeYelp)? publisherIdsWithoutYelp.concat(YelpEnterprisePublisherId) : publisherIds;

  return corePublishers.concat(LlmPublisherId, ExtendedNetworkPublisherId);
}
